import { useRef, useState } from "react";
import { Camera, Image as ImageIcon, X } from "lucide-react";
import FeedbackAnnotator from "./FeedbackAnnotator";

// Two ways to attach an image:
//   1. Capture this page  → html2canvas snapshots document.body, hiding
//      the feedback widget itself first so it doesn't appear in the shot.
//   2. Upload from device  → standard <input type=file accept=image/*>
//
// Either way → annotator opens with the image loaded. User draws, hits
// "Use this" → onPicked(blob) fires with the flattened PNG. Parent
// uploads it to the backend.
export default function FeedbackImagePicker({ onPicked, onCancel }) {
  const [imageSrc, setImageSrc] = useState(null); // data URL for annotator
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const captureScreen = async () => {
    setBusy(true); setError("");
    // Hide the feedback widget chrome so it doesn't appear in the screenshot.
    // CRUCIAL: restore in finally — if capture throws, widget would stay
    // hidden and the user would see nothing happen.
    const widgets = document.querySelectorAll("[data-zcd-feedback-chrome]");
    widgets.forEach((el) => { el.style.visibility = "hidden"; });

    try {
      // html2canvas-pro is a drop-in replacement for the unmaintained
      // html2canvas 1.4.1 — adds support for modern CSS colour functions
      // (oklch / oklab / lab / lch) which Tailwind v4 emits everywhere.
      // Original html2canvas throws a parse error on the first oklch().
      const { default: html2canvas } = await import("html2canvas-pro");
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        logging: false,
        backgroundColor: "#0b0b1a", // matches our dark theme background
        scale: window.devicePixelRatio > 1 ? 2 : 1,
        windowWidth: document.documentElement.clientWidth,
        windowHeight: document.documentElement.clientHeight,
        // Skip the widget itself in case the data-attribute hide didn't apply
        ignoreElements: (el) => el.hasAttribute && el.hasAttribute("data-zcd-feedback-chrome"),
      });
      setImageSrc(canvas.toDataURL("image/png"));
    } catch (e) {
      console.error("page capture failed:", e);
      setError(`Couldn't capture the page (${e?.message?.slice(0, 80) || "unknown error"}). Try Upload from device instead.`);
    } finally {
      // ALWAYS restore visibility, even on error
      widgets.forEach((el) => { el.style.visibility = ""; });
      setBusy(false);
    }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      setError("File too large (max 8 MB)."); return;
    }
    const reader = new FileReader();
    reader.onload = () => setImageSrc(reader.result);
    reader.onerror = () => setError("Couldn't read the file.");
    reader.readAsDataURL(file);
  };

  if (imageSrc) {
    return (
      <FeedbackAnnotator
        imageSrc={imageSrc}
        onAccept={(blob) => { setImageSrc(null); onPicked(blob); }}
        onCancel={() => setImageSrc(null)}
      />
    );
  }

  return (
    <div className="p-4 flex flex-col gap-3" data-zcd-feedback-chrome>
      <p className="text-sm text-text-primary">
        Attach a screenshot. Capture the current page or upload one — then mark it up with a pen.
      </p>
      <div className="flex flex-col gap-2">
        <button
          onClick={captureScreen}
          disabled={busy}
          className="flex items-center justify-center gap-2 px-3 py-2 rounded bg-button-primary text-white text-sm hover:opacity-90 disabled:opacity-50"
        >
          <Camera className="w-4 h-4" />
          {busy ? "Capturing…" : "Capture this page"}
        </button>
        <button
          onClick={() => fileRef.current?.click()}
          className="flex items-center justify-center gap-2 px-3 py-2 rounded bg-bg-secondary text-text-primary text-sm hover:bg-bg-secondary/70"
        >
          <ImageIcon className="w-4 h-4" />
          Upload from device
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={onFile}
          className="hidden"
        />
      </div>
      {error && (
        <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1">
          {error}
        </div>
      )}
      <button
        onClick={onCancel}
        className="text-xs text-text-secondary hover:text-text-primary self-end flex items-center gap-1"
      >
        <X className="w-3 h-3" /> Cancel
      </button>
    </div>
  );
}
